<script setup lang="ts">
import { computed, defineAsyncComponent, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import { applyCommunityBranding, clearCommunityBranding } from '@/branding/runtime';
import HomepageItemPrivateNoAccessDialog from '@/components/dialogs/HomepageItemPrivateNoAccessDialog.vue';
import { useProjectPage } from '@/composables/useProjectPage';
import { useCommunityStore } from '@/store/communityStore';
import { useConfigurationStore } from '@/store/configurationStore';
import { themeRegistry } from '@/themes/registry';
import { scrollToElement } from '@/utils/scroll-util';

const props = defineProps<{ isTemplate: boolean }>();

const route = useRoute();
const communityStore = useCommunityStore();
const configurationStore = useConfigurationStore();
const slug = route.params.slug as string;
const { t } = useI18n();

const {
  project,
  modules,
  banner,
  themeKey,
  isLocaleSupported,
  showNoAccessDialog,
  isLoading,
  currentLocale,
  userAccess,
  homeLink,
  needLogin,
  loadProject,
} = useProjectPage(slug, props.isTemplate);

const layoutComponent = computed(() => defineAsyncComponent(themeRegistry.getLayout('project', themeKey.value)));
const designSystem = computed(() => themeRegistry.getDesignSystem('project', themeKey.value));
const canRenderLayout = computed(() => Boolean(project.value) && (props.isTemplate || userAccess.value.allowed));
const homepageItemPrivateNoAccessDialogRef = ref<InstanceType<typeof HomepageItemPrivateNoAccessDialog>>();
let hashScrollTimeouts: number[] = [];

function scrollToRouteHash(attempt = 0) {
  const hash = route.hash?.replace(/^#/, '');
  if (!hash) return;

  const target = document.getElementById(hash);
  if (target) {
    scrollToElement(hash, 'auto');
    return;
  }

  if (attempt >= 10) return;

  window.setTimeout(() => {
    scrollToRouteHash(attempt + 1);
  }, 100);
}

function clearHashScrollTimeouts() {
  hashScrollTimeouts.forEach((timeoutId) => window.clearTimeout(timeoutId));
  hashScrollTimeouts = [];
}

function stabilizeRouteHashScroll() {
  const hash = route.hash?.replace(/^#/, '');
  if (!hash) return;

  clearHashScrollTimeouts();
  scrollToRouteHash();

  [150, 350, 700, 1200].forEach((delay) => {
    const timeoutId = window.setTimeout(() => {
      scrollToRouteHash();
    }, delay);
    hashScrollTimeouts.push(timeoutId);
  });
}

watch(
  [() => showNoAccessDialog.value, () => isLoading.value],
  async ([show, loading]) => {
    if (!show || loading) {
      return;
    }

    await nextTick();
    homepageItemPrivateNoAccessDialogRef.value?.openDialog();
  },
  { immediate: true },
);

watch(
  [() => route.hash, () => isLoading.value, () => canRenderLayout.value, () => modules.value.length],
  async ([hash, loading, canRender]) => {
    if (!hash || loading || !canRender) {
      return;
    }

    await nextTick();
    stabilizeRouteHashScroll();
  },
  { immediate: true },
);

onMounted(() => {
  loadProject();
});

watch(
  [() => designSystem.value, () => communityStore.community, () => configurationStore.brandingPresets],
  async ([currentDesignSystem, currentCommunity]) => {
    if (currentDesignSystem !== 'modern') {
      clearCommunityBranding();
      return;
    }

    await applyCommunityBranding(currentCommunity);
  },
  { immediate: true, deep: true },
);

onBeforeUnmount(() => {
  clearHashScrollTimeouts();
  clearCommunityBranding();
});
</script>

<template>
  <div
    v-if="!isLoading"
    class="project-view"
    :class="`project-view--${designSystem}`"
    :data-design-system="designSystem"
  >
    <component
      v-if="canRenderLayout"
      :is="layoutComponent"
      :project="project"
      :modules="modules"
      :banner="banner"
      :theme-key="themeKey"
      :is-template="props.isTemplate"
      :is-locale-supported="isLocaleSupported"
      :current-locale="currentLocale"
    />
    <HomepageItemPrivateNoAccessDialog
      v-if="project"
      ref="homepageItemPrivateNoAccessDialogRef"
      :home-link="homeLink"
      :need-login="needLogin"
      :item="project"
      :language-enabled="project?.languageEnabled"
    />
  </div>
  <div v-else class="loading">
    {{ t('loading') }}
  </div>
</template>

<style lang="scss" scoped>
.project-view {
  min-height: 100%;
}

.loading {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 50vh;
}
</style>
